import React, { useState } from 'react';
type StatRow = {
  key: string;
  label: string;
  group: string;
  minutes: Record<number, number>;
};
const MINUTES = Array.from({
  length: 48
}, (_, i) => i + 1);
const QUARTER_LENGTH = 12;
const statRows: StatRow[] = [{
  key: 'pts',
  label: 'PTS',
  group: 'Scoring',
  minutes: {
    2: 2,
    5: 3,
    8: 2,
    15: 2,
    18: 1,
    19: 1,
    22: 3,
    30: 2,
    33: 2,
    41: 3,
    45: 2,
    47: 2
  }
}, {
  key: 'fg2m',
  label: 'FG2M',
  group: 'Scoring',
  minutes: {
    2: 1,
    8: 1,
    15: 1,
    30: 1,
    33: 1,
    45: 1,
    47: 1
  }
}, {
  key: 'fg2a',
  label: 'FG2A',
  group: 'Scoring',
  minutes: {
    2: 1,
    5: 1,
    8: 2,
    15: 1,
    22: 1,
    30: 1,
    33: 2,
    45: 1,
    47: 1
  }
}, {
  key: 'fg3m',
  label: 'FG3M',
  group: 'Scoring',
  minutes: {
    5: 1,
    22: 1,
    41: 1
  }
}, {
  key: 'fg3a',
  label: 'FG3A',
  group: 'Scoring',
  minutes: {
    5: 1,
    12: 1,
    22: 1,
    35: 1,
    41: 1,
    44: 1
  }
}, {
  key: 'ftm',
  label: 'FTM',
  group: 'Scoring',
  minutes: {
    18: 1,
    19: 1
  }
}, {
  key: 'fta',
  label: 'FTA',
  group: 'Scoring',
  minutes: {
    18: 1,
    19: 1,
    26: 1
  }
}, {
  key: 'oreb',
  label: 'OREB',
  group: 'Rebounding',
  minutes: {
    9: 1,
    31: 1
  }
}, {
  key: 'dreb',
  label: 'DREB',
  group: 'Rebounding',
  minutes: {
    3: 1,
    7: 1,
    16: 1,
    23: 1,
    29: 1,
    38: 1,
    46: 1
  }
}, {
  key: 'ast',
  label: 'AST',
  group: 'Playmaking',
  minutes: {
    6: 1,
    17: 1,
    21: 1,
    34: 1,
    42: 1
  }
}, {
  key: 'tov',
  label: 'TOV',
  group: 'Playmaking',
  minutes: {
    11: 1,
    25: 1,
    43: 1
  }
}, {
  key: 'stl',
  label: 'STL',
  group: 'Defense',
  minutes: {
    4: 1,
    28: 1
  }
}, {
  key: 'blk',
  label: 'BLK',
  group: 'Defense',
  minutes: {
    36: 1
  }
}, {
  key: 'pf',
  label: 'PF',
  group: 'Defense',
  minutes: {
    13: 1,
    27: 1,
    44: 1
  }
}];

// Season per-game averages, used as the reference next to the game total
const seasonAverages: Record<string, number> = {
  pts: 22.4,
  fg2m: 6.2,
  fg2a: 10.4,
  fg3m: 2.6,
  fg3a: 6.8,
  ftm: 3.1,
  fta: 3.9,
  oreb: 1.5,
  dreb: 6.1,
  ast: 4.4,
  tov: 2.3,
  stl: 1.2,
  blk: 0.6,
  pf: 2.4
};
const LEGEND = [{
  label: 'None',
  className: 'bg-white'
}, {
  label: 'Low',
  className: 'bg-blue-100'
}, {
  label: 'Medium',
  className: 'bg-blue-300'
}, {
  label: 'High',
  className: 'bg-blue-500'
}];
const rowTotal = (row: StatRow) => Object.values(row.minutes).reduce((sum, v) => sum + v, 0);
const cellClasses = (value: number, max: number) => {
  if (value <= 0) return 'bg-white text-gray-300';
  const ratio = value / Math.max(max, 1);
  if (ratio >= 0.99) return 'bg-blue-500 text-white';
  if (ratio >= 0.6) return 'bg-blue-300 text-gray-900';
  return 'bg-blue-100 text-gray-900';
};
const PlayerTimeline = () => {
  const [activeGroup, setActiveGroup] = useState<string>('All');
  const groups = ['All', ...Array.from(new Set(statRows.map(row => row.group)))];
  const visibleRows = activeGroup === 'All' ? statRows : statRows.filter(row => row.group === activeGroup);
  const activityPerMinute = (minute: number) => visibleRows.filter(row => row.key !== 'pts').reduce((sum, row) => sum + (row.minutes[minute] || 0), 0);
  return <section aria-label="Game timeline" data-id="element-772" data-mp="1c89mgcjw67fw">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mb-4 p-4 bg-gray-50 border rounded-lg" data-id="element-773" data-mp="yc5w4uc52j8p">
        <div className="flex items-center bg-white border rounded-md p-0.5" role="group" aria-label="Stat group filter" data-id="element-774" data-mp="139eil74bl9fr">
          {groups.map(group => <button key={group} onClick={() => setActiveGroup(group)} aria-pressed={activeGroup === group} className={`px-3 py-1 text-xs font-bold rounded transition-colors ${activeGroup === group ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-775" data-mp="qaov6g2vty6l">
              {group}
            </button>)}
        </div>

        <div className="flex items-center gap-1.5" data-id="element-776" data-mp="7i2t3e1dtbdk">
          {LEGEND.map(step => <span key={step.label} title={step.label} className={`w-6 h-6 rounded flex items-center justify-center text-[10px] font-bold border text-gray-900 ${step.className}`} data-id="element-777" data-mp="2lce6u1bcltq">
              {step.label.charAt(0)}
            </span>)}
        </div>

        <p className="text-xs text-gray-500" data-id="element-778" data-mp="1sxrylp4okmhi">
          Darker cells mean more of that stat recorded in the minute
        </p>
      </div>

      {/* Heatmap */}
      <div className="overflow-x-auto border rounded-lg bg-white" data-id="element-779" data-mp="3fswvx6ya11i1">
        <table className="min-w-full border-collapse text-sm" data-id="element-780" data-mp="3kkevbqkniu0w">
          <caption className="sr-only" data-id="element-781" data-mp="3kl87x86akdqy">
            Player stats recorded minute by minute
          </caption>
          <thead data-id="element-782" data-mp="3oo4iqcplb5kc">
            <tr className="bg-gray-50" data-id="element-783" data-mp="2rvr83pgcdukp">
              <th scope="col" className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-gray-500 w-20" data-id="element-784" data-mp="2rwsu2oywc1h7">
                Stat
              </th>
              {MINUTES.map(minute => <th key={minute} scope="col" className={`w-8 px-0 py-2 text-center text-[10px] font-bold text-gray-500 ${minute % QUARTER_LENGTH === 0 ? 'border-r border-gray-300' : ''}`} data-id="element-785" data-mp="35vsipwmesgdd">
                  {minute}
                </th>)}
              <th scope="col" className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold text-gray-500 border-l border-gray-300" data-id="element-786" data-mp="2h6v4rxozrs61">
                Tot
              </th>
              <th scope="col" className="px-3 py-2 text-center text-[10px] uppercase tracking-wider font-bold text-gray-500 border-l border-gray-200" data-id="element-787" data-mp="2r49i3ofmc0u4">
                Avg.
              </th>
            </tr>
          </thead>

          <tbody data-id="element-788" data-mp="1tkonslxlb23o">
            {visibleRows.map((row, index) => {
            const total = rowTotal(row);
            const max = Math.max(...Object.values(row.minutes), 1);
            const isGroupStart = index === 0 || visibleRows[index - 1].group !== row.group;
            return <tr key={row.key} className={isGroupStart ? 'border-t-2 border-gray-300' : 'border-t border-gray-100'} data-id="element-789" data-mp="1fpdv9f6tit2h" data-mpi={index}>
                  <th scope="row" className="sticky left-0 z-10 bg-white px-3 py-1.5 text-left whitespace-nowrap text-xs font-bold text-gray-900 w-20" data-id="element-790" data-mp="3r7hcumxcy46m">
                    {row.label}
                  </th>

                  {MINUTES.map(minute => {
                const value = row.minutes[minute] || 0;
                return <td key={minute} title={value > 0 ? `${row.label} — minute ${minute}: ${value}` : undefined} className={`w-8 h-8 text-center text-xs font-bold border-r border-gray-100 ${minute % QUARTER_LENGTH === 0 ? 'border-r-gray-300' : ''} ${cellClasses(value, max)}`} data-id="element-791" data-mp="dymfhuj9ej3c">
                        {value > 0 ? value : ''}
                      </td>;
              })}

                  <td className="px-3 py-1.5 text-center text-xs font-bold text-gray-900 border-l border-gray-300" data-id="element-792" data-mp="3cd5e20k5bm14">
                    {total}
                  </td>
                  <td className="px-3 py-1.5 text-center text-xs font-medium text-gray-600 border-l border-gray-200" data-id="element-793" data-mp="32g0sa1u7psku">
                    {(seasonAverages[row.key] ?? 0).toFixed(1)}
                  </td>
                </tr>;
          })}
          </tbody>

          <tfoot data-id="element-794" data-mp="3v6shgfcv1gyo">
            <tr className="border-t-2 border-gray-300 bg-gray-50" data-id="element-795" data-mp="3qdg6v950naox">
              <th scope="row" className="sticky left-0 z-10 bg-gray-50 px-3 py-2 text-left text-[10px] uppercase tracking-wider font-bold text-gray-500 whitespace-nowrap" data-id="element-796" data-mp="3qbmxvn6xkedr">
                Activity
              </th>
              {MINUTES.map(minute => {
              const value = activityPerMinute(minute);
              return <td key={minute} className={`w-8 px-0 py-2 text-center text-xs font-bold ${minute % QUARTER_LENGTH === 0 ? 'border-r border-gray-300' : ''} ${value > 0 ? 'text-gray-900' : 'text-gray-300'}`} data-id="element-797" data-mp="pavp1dftb567">
                    {value > 0 ? value : '·'}
                  </td>;
            })}
              <td className="px-3 py-2 text-center text-xs font-bold text-gray-900 border-l border-gray-300" data-id="element-798" data-mp="186vkiy800quj">
                {visibleRows.filter(row => row.key !== 'pts').reduce((sum, row) => sum + rowTotal(row), 0)}
              </td>
              <td className="px-3 py-2 text-center text-xs font-medium text-gray-600 border-l border-gray-200" data-id="element-799" data-mp="1mwjjhsspjdf3">
                {visibleRows.filter(row => row.key !== 'pts').reduce((sum, row) => sum + (seasonAverages[row.key] ?? 0), 0).toFixed(1)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>;
};
export default PlayerTimeline;